import { useMemo } from "react";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  buildVoiceStudyBackPrompt,
  defaultVoiceStudyRating,
  getVoiceStudyCard,
  useVoiceStudyActiveSession,
  useVoiceStudyTurn,
  type VoiceStudyTurn,
} from "@-/dev.improve.haniverse.lib.voice-study";
import { getPinyinMp3Asset } from "@-/dev.improve.haniverse.assets.pinyin.audio";
import { tonelessSyllableForPinyinAssetLookup } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { useIsPremium } from "@-/dev.improve.haniverse.components.superwall";
import { LearnDeckContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import {
  DrillPageContext,
  GraphemeDrillContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useReviewCardFsrsResolutionQueryOptions } from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import { useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import {
  useIsRating,
  useRatingMutation,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.rating";

/** A grapheme drill's revealed back as a voice-study turn, while voice study is on. */
export function VoiceStudyFlashcardBack() {
  const session = useVoiceStudyActiveSession();
  return session !== null && <ActiveVoiceStudyFlashcardBack />;
}

function ActiveVoiceStudyFlashcardBack() {
  const isPremium = useIsPremium();
  const [character] = useContextSelectors(DrillPageContext, (c) => c.character);
  const [card, drillType] = useContextSelectors(
    GraphemeDrillContext,
    (c) => c.slice.data,
    (c) => c.drillType,
  );
  // Plain queries (the deck prepares both): a card with no FSRS row yet simply is not eligible.
  const { data: fsrs = null } = useQuery(
    useReviewCardFsrsResolutionQueryOptions(character, drillType),
  );
  const { data: userSlice } = useQuery(
    useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions(character),
  );
  const ownMnemonic = userSlice?.meaningCard?.description;
  const [presetRating] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.answerInputState.presetRating,
  );
  const [commit, flip] = useContextSelectors(
    LearnDeckContext,
    (c) => c.commit,
    (c) => c.flip,
  );
  const focused = useIsFocused();
  const ratingMutation = useRatingMutation("voice");
  const ratingPending = useIsRating();
  const clue = drillType.kind === "meaning" ? getVoiceStudyCard(card) : null;
  const eligible = clue && fsrs;

  const studyPinyinList = usePrevalentPinyin<{ toneNumbersPinyin: string }>(
    character,
    card.pinyinList,
  );
  const pinyinKey = studyPinyinList[0]?.toneNumbersPinyin;
  const clip = useMemo(
    () => (pinyinKey === undefined ? null : getPinyinMp3Asset(pinyinKey)),
    [pinyinKey],
  );
  const syllable = pinyinKey === undefined ? "" : tonelessSyllableForPinyinAssetLookup(pinyinKey);
  // Same gate as the mnemonics panel: the catalog mnemonic and description are premium together.
  const catalogVisible = !card.isDescriptionPremium || isPremium;
  const mnemonic = ownMnemonic?.trim() || (catalogVisible ? card.mnemonic : null);
  const description = catalogVisible ? card.description : null;
  const mnemonicTerm = card.mnemonicTerms[0];
  const prompt = useMemo(
    () =>
      clue
        ? buildVoiceStudyBackPrompt({
            card: clue,
            clip,
            description,
            mnemonic,
            mnemonicTerm,
            syllable,
          })
        : null,
    [clip, clue, description, mnemonic, mnemonicTerm, syllable],
  );

  const turn: VoiceStudyTurn | null =
    eligible && prompt
      ? {
          glyph: character,
          id: `${character}:meaning:back`,
          onCommand: async (command) => {
            if (!focused || ratingPending) {
              return { kind: "handled" };
            }
            switch (command.kind) {
              case "next": {
                await ratingMutation.mutateAsync(defaultVoiceStudyRating(presetRating));
                return { kind: "handled" };
              }
              case "rating": {
                await ratingMutation.mutateAsync(command.rating);
                return { kind: "handled" };
              }
              case "skip": {
                // Defer without an FSRS write: the next-due read deprioritises this grapheme.
                commit({ kind: "continue" }, "voice");
                return { kind: "handled" };
              }
              case "unreveal": {
                // Back to the prompt: the deck flips the card; there is no back route to pop.
                flip("front");
                return { kind: "handled" };
              }
              default: {
                return { kind: "unhandled" };
              }
            }
          },
          prompt,
          side: "back",
        }
      : null;
  useVoiceStudyTurn(turn);
  return null;
}
